export type MarkdownBlock =
  | { type: 'markdown'; text: string }
  | { type: 'table'; headers: string[]; rows: string[][] };

const splitRow = (line: string): string[] => {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((cell) => cell.trim());
};

const isSeparator = (line: string): boolean => {
  if (!line.includes('-')) return false;
  const cells = splitRow(line);
  return (
    cells.length > 0 &&
    cells.every((cell) => /^:?-+:?$/.test(cell.replace(/\s/g, '')))
  );
};

const looksLikeRow = (line: string): boolean =>
  line.trim().startsWith('|') || /\|/.test(line);

export const splitMarkdownBlocks = (content: string): MarkdownBlock[] => {
  const lines = content.split('\n');
  const blocks: MarkdownBlock[] = [];
  let buffer: string[] = [];

  const flushMarkdown = () => {
    const text = buffer.join('\n').trim();
    buffer = [];
    if (text) blocks.push({ type: 'markdown', text });
  };

  for (let index = 0; index < lines.length; index += 1) {
    const header = lines[index];
    const separator = lines[index + 1];
    if (looksLikeRow(header) && separator && isSeparator(separator)) {
      flushMarkdown();
      const headers = splitRow(header);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && looksLikeRow(lines[index])) {
        const cells = splitRow(lines[index]);
        if (cells.some((cell) => cell.length > 0)) rows.push(cells);
        index += 1;
      }
      index -= 1;
      blocks.push({ type: 'table', headers, rows });
      continue;
    }
    buffer.push(header);
  }

  flushMarkdown();
  return blocks;
};

export const isNumericLabel = (value: string): boolean =>
  /^(tracks|#|bpm|key|count|plays)$/i.test(value.trim());

export const isNumericValue = (value: string): boolean =>
  /^[\d.]+%?$/.test(value.trim());

export const extractCellHref = (cell: string): string | null => {
  const markdown = cell.match(/\[[^\]]*\]\(([^)\s]+)\)/);
  if (markdown?.[1]) return markdown[1];
  const raw = cell.match(
    /\b((?:ch|serato|vdj|spotify|tidal|apple):\/\/[^\s)]+)/i,
  );
  return raw?.[1] ?? null;
};

const WHOLE_CELL_LINK = /^\[([^\]]*)\]\(([^)\s]+)\)$/;
const WHOLE_CELL_URL = /^(?:ch|serato|vdj|spotify|tidal|apple):\/\/\S+$/i;

/**
 * A cell that is nothing but one link, which can render as an icon and a
 * title. Undefined for anything else — a cell holding a link plus a comment
 * has to go through the markdown renderer, or the prose and the link syntax
 * around it end up as the link's own text.
 */
export const cellLink = (
  cell: string,
): { href: string; label: string } | undefined => {
  const trimmed = cell.trim();

  const markdown = trimmed.match(WHOLE_CELL_LINK);
  if (markdown) return { href: markdown[2], label: markdown[1] };

  if (WHOLE_CELL_URL.test(trimmed)) return { href: trimmed, label: trimmed };

  return undefined;
};

export type ParsedTrackRow = {
  id?: string;
  artist: string;
  title: string;
  bpm?: number;
  key?: string;
  match?: string;
  energy?: number;
  danceability?: number;
  valence?: number;
  trackNum?: number;
};

const cellText = (cell: string): string => {
  const match = cell.trim().match(/^\[([^\]]*)\]\(([^)]+)\)$/);
  return (match?.[1] ?? cell).replace(/\*\*/g, '').trim();
};

const headerKey = (header: string): string =>
  header
    .trim()
    .toLowerCase()
    .replace(/[^a-z#]/g, '');

const columnIndex = (headers: string[], names: string[]): number =>
  headers.findIndex((header) => names.includes(headerKey(header)));

const parseNumber = (value: string): number | undefined => {
  const parsed = Number(value.replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const splitArtistTitle = (label: string): { artist: string; title: string } => {
  const parts = label.split(/\s+[—–-]\s+/);
  if (parts.length >= 2) {
    return {
      artist: parts[0].trim(),
      title: parts.slice(1).join(' — ').trim(),
    };
  }
  return { artist: '', title: label };
};

const trackIdFromHref = (href: string | null): string | undefined => {
  if (!href) return undefined;
  const match = href.match(/^ch:\/\/track\/(.+)$/i);
  return match?.[1]
    ? decodeURIComponent(match[1]).replace(/\/$/, '')
    : undefined;
};

/** Every column a track row can actually show. */
const TRACK_ROW_COLUMNS = [
  'track',
  'title',
  'song',
  'artist',
  'bpm',
  'key',
  'energy',
  'dance',
  'danceability',
  'mood',
  'valence',
  'match',
  '#',
  'pos',
  'position',
];

export const isTrackTable = (headers: string[], rows: string[][]): boolean => {
  const keys = headers.map(headerKey);
  if (keys.some((key) => ['crate', 'crates', 'playlist', 'kind'].includes(key)))
    return false;

  const hrefs = rows.flatMap((row) =>
    row.map((cell) => extractCellHref(cell) ?? ''),
  );
  if (
    hrefs.some((href) =>
      /^(ch|serato|vdj|spotify|tidal|apple):\/\/(crate|mine|example|playlist)/i.test(
        href,
      ),
    )
  ) {
    return false;
  }

  // A track row shows artwork, title, artist, key and BPM, and nothing else.
  // Any other column is the point the answer is making — collapsing that into
  // track rows drops it and offers crate actions the question never asked
  // for, so the table stays a table.
  if (!keys.every((key) => TRACK_ROW_COLUMNS.includes(key))) return false;

  // Track links are the strongest signal, but only once the columns fit.
  if (hrefs.some((href) => /ch:\/\/track/i.test(href))) return true;

  const trackLike = keys.some((key) =>
    ['track', 'title', 'song', 'artist'].includes(key),
  );
  const mixLike = keys.some((key) => key === 'bpm' || key === 'key');
  return trackLike && mixLike;
};

export const parseTrackRows = (
  headers: string[],
  rows: string[][],
): ParsedTrackRow[] => {
  const keys = headers.map(headerKey);
  const trackCol = columnIndex(keys, ['track', 'title', 'song']);
  const artistCol = columnIndex(keys, ['artist']);
  const titleCol = columnIndex(keys, ['title', 'song']);
  const bpmCol = columnIndex(keys, ['bpm']);
  const keyCol = columnIndex(keys, ['key']);
  const energyCol = columnIndex(keys, ['energy']);
  const danceCol = columnIndex(keys, ['dance', 'danceability']);
  const moodCol = columnIndex(keys, ['mood', 'valence']);
  const matchCol = columnIndex(keys, ['match']);
  const numCol = columnIndex(keys, ['#', 'pos', 'position']);

  return rows.map((row, index) => {
    const linked = row.map((cell) => extractCellHref(cell));
    const id = linked.map(trackIdFromHref).find(Boolean);
    const trackCell = cellText(row[trackCol] ?? row[0] ?? '');
    const artistCell =
      artistCol >= 0 ? cellText(row[artistCol] ?? '') : undefined;
    const titleCell = titleCol >= 0 ? cellText(row[titleCol] ?? '') : undefined;
    const split = splitArtistTitle(trackCell);
    const artist = artistCell || split.artist;
    const title =
      titleCol >= 0 && titleCol !== trackCol
        ? titleCell || split.title
        : split.title || trackCell;

    return {
      id,
      artist,
      title,
      bpm: bpmCol >= 0 ? parseNumber(cellText(row[bpmCol] ?? '')) : undefined,
      key: keyCol >= 0 ? cellText(row[keyCol] ?? '') || undefined : undefined,
      match:
        matchCol >= 0 ? cellText(row[matchCol] ?? '') || undefined : undefined,
      energy:
        energyCol >= 0
          ? parseNumber(cellText(row[energyCol] ?? ''))
          : undefined,
      danceability:
        danceCol >= 0 ? parseNumber(cellText(row[danceCol] ?? '')) : undefined,
      valence:
        moodCol >= 0 ? parseNumber(cellText(row[moodCol] ?? '')) : undefined,
      trackNum:
        numCol >= 0 ? parseNumber(cellText(row[numCol] ?? '')) : index + 1,
    };
  });
};
